import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../providers/authProvider';
import { useAuth } from '../../../contexts/AuthContext';
import devInstanceAccessService from '../../../services/DevInstanceAccessService';
import { Button } from "../../../components/ui/button";
import { ArrowLeft } from "lucide-react";
import AdaptLogo from "../../../images/Adapt_Logo.jpg";

const SSOLogin = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSSOLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // If email is provided, use it as a login hint
      const loginHint = email ? { loginHint: email } : {};
      const requestWithHint = { ...loginRequest, ...loginHint };

      // Perform popup login with Azure AD
      const response = await instance.loginPopup(requestWithHint);
      
      if (response.account) {
        // Extract user information
        const userDisplayName = response.account.name || response.account.username;
        const userEmail = response.account.username;
        
        // Log success message
        console.log(`✅ SSO Access granted! Welcome to Adapt Learning, ${userDisplayName}`);
        
        // Set authentication state in context
        authLogin({
          user: userDisplayName,
          email: userEmail,
          loginType: 'Azure AD SSO',
          accessibleInstances: [], // Will be loaded on instances page
          verificationResult: null
        });
        
        // Navigate to hub page (beta) or instances page, based on allowlist
        navigate(await devInstanceAccessService.getPostLoginRoute(userEmail));
      }
    } catch (error) {
      console.error('SSO Login failed:', error);
      if (error.errorCode === 'user_cancelled') {
        console.log('User cancelled the login flow');
      } else {
        console.error("❌ Access denied. User should contact learning administrator or request access to Adapt Learning.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  return (
    <section className="min-h-screen bg-white flex items-center justify-center p-4">
      {/* Main Login Container - Framed Layout */}
      <div className="w-full max-w-6xl bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col lg:flex-row" style={{ minHeight: '700px', maxHeight: '720px' }}>
        
        {/* Left Panel - Image Section */}
        <div className="lg:w-1/2 bg-gradient-to-br from-cyan-400 to-cyan-600 relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-cyan-500 bg-opacity-10"></div>
          <div className="relative z-10 p-8 text-center text-white max-w-md">
            {/* Logo Display */}
            <div className="mb-6">
              <div className="w-32 h-32 lg:w-40 lg:h-40 mx-auto mb-6 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center overflow-hidden">
                <img 
                  src={AdaptLogo}
                  alt="Adapt Logo" 
                  className="w-full h-full object-contain rounded-2xl"
                />
              </div>
            </div>
            
            <h1 className="text-2xl lg:text-3xl font-bold leading-tight">
              Learn Anywhere,<br />
              Anytime!<br />
              <span className="block mt-2">
                Access Learning
              </span>
            </h1>
          </div>
        </div>

        {/* Right Panel - SSO Login Form */}
        <div className="lg:w-1/2 bg-white flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Back Button */}
            <div className="mb-4">
              <Button
                variant="ghost"
                onClick={handleGoBack}
                className="text-gray-600 hover:text-gray-800 p-0"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            {/* Header Section */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-semibold text-gray-800 mb-2">Login</h1>
              <p className="text-gray-600">Enter your corporate email to access Adapt Learning</p>
            </div>

          <form onSubmit={handleSSOLogin} className="space-y-4">
            <div className="mb-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your corporate email"
                className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none bg-gray-50 text-gray-900"
                style={{ height: '50px', fontSize: '14px', fontWeight: '600' }}
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full text-white py-3 text-lg font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              style={{ 
                height: '50px', 
                backgroundColor: '#098C94', 
                fontSize: '20px',
                boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.04)'
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Authenticating...
                </div>
              ) : (
                'Login with SSO'
              )}
            </Button>
          </form>

          <div className="text-center">
            <p 
              className="text-sm text-cyan-600 hover:text-cyan-700 cursor-pointer"
              onClick={handleGoBack}
            >
              Don't have a corporate email? Go back
            </p>
          </div>

          {/* Demo Account Details - Hidden for production
          <div className="text-center text-xs text-gray-500 mt-4">
            <p>Demo Accounts:</p>
            <p>vadiraj.joshi@laerdal.com</p>
            <p>lokesh.krishnappa@laerdal.com</p>
          </div>
          */}
        </div>
      </div>
    </div>
    </section>
  );
};

export default SSOLogin;
